import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Crown, Sparkles, LayoutGrid, Users, Gift, Layers, Rocket,
  X, Check, CreditCard, RefreshCw,
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';

const SITE_URL = 'https://bolaoelite.com.br';
const PREMIUM_PRICE_BRL = 9.90;

export default function Premium() {
  const { t } = useTranslation('institutional');
  const { limits } = usePublicFreeLimits();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const poolNoun =
    limits.maxPrivatePoolsCreated === 1
      ? t('premium.pool_singular', 'bolão privado')
      : t('premium.pool_plural', 'bolões privados');

  // JSON-LD Product + Offer — sinal forte de "site comercial real" para
  // o crawler do AdSense (equivalente ao schema que WooCommerce emite).
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${SITE_URL}/premium#product`,
        name: 'Bolão Elite Premium',
        description:
          'Plano pago opcional do Bolão Elite. Remove os limites do plano gratuito — bolões privados e participantes ilimitados — e tira os anúncios da plataforma. R$ 9,90/mês, cancelável a qualquer momento.',
        url: `${SITE_URL}/premium`,
        brand: { '@type': 'Brand', name: 'Bolão Elite' },
        category: 'Subscription / SaaS',
        offers: {
          '@type': 'Offer',
          '@id': `${SITE_URL}/premium#offer`,
          url: `${SITE_URL}/premium`,
          priceCurrency: 'BRL',
          price: PREMIUM_PRICE_BRL.toFixed(2),
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: PREMIUM_PRICE_BRL.toFixed(2),
            priceCurrency: 'BRL',
            unitCode: 'MON',
            unitText: 'mês',
          },
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          eligibleRegion: { '@type': 'Country', name: 'BR' },
          seller: { '@type': 'Organization', name: 'Bolão Elite' },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Quanto custa o Bolão Elite Premium?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A assinatura Premium custa R$ 9,90 por mês, no cartão de crédito, com renovação automática. Pode cancelar a qualquer momento.',
            },
          },
          {
            '@type': 'Question',
            name: 'O que muda quando eu assino o Premium?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Como Premium você cria bolões privados ilimitados, com quantos participantes quiser em cada um, navega sem anúncios e participa de quantos bolões de outras pessoas desejar.',
            },
          },
          {
            '@type': 'Question',
            name: 'Os participantes dos meus bolões também precisam ser Premium?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Não. Qualquer pessoa com conta gratuita pode entrar nos seus bolões normalmente. O Premium é apenas do criador — quem participa não paga nada.',
            },
          },
          {
            '@type': 'Question',
            name: 'Posso cancelar a qualquer momento?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sim. O cancelamento é feito pelo próprio usuário na página de assinatura. Você mantém os benefícios Premium até a data de validade já paga e depois a conta volta ao plano gratuito.',
            },
          },
        ],
      },
    ],
    []
  );

  const seo = useLocalizedSeo('premium');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('premium.seo_title', 'Bolão Elite Premium — Bolões e participantes ilimitados, sem anúncios')}
        description={t('premium.seo_desc', 'Conheça o Bolão Elite Premium: bolões privados ilimitados, participantes ilimitados e navegação sem anúncios por R$ 9,90/mês. Cancele quando quiser.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="bolão elite premium, assinatura, bolões ilimitados, sem anúncios, plano premium futebol"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/10 rounded-full" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-white/5 rounded-full" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm text-white/70 mb-4" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('premium.breadcrumb', 'Premium')}</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 mb-6">
            <Crown className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">{t('premium.badge', 'Plano Premium')}</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {t('premium.h1', 'Seu bolão sem limites')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/90 leading-relaxed max-w-3xl">
            {t('premium.hero_text', 'Crie quantos bolões quiser, com quantos participantes quiser, e navegue sem nenhum anúncio. Por menos do que um café por mês — e cancelável a qualquer momento.')}
          </p>
          <div className="mt-8 flex flex-wrap items-end gap-3">
            <div className="flex items-baseline gap-1">
              <span className="text-sm text-white/70">R$</span>
              <span className="text-5xl font-black tabular-nums">{t('premium.price_amount', '9,90')}</span>
              <span className="text-sm text-white/70 ml-1">{t('premium.per_month', '/mês')}</span>
            </div>
            <Link
              to="/assinatura"
              className="inline-block px-8 py-3 bg-white text-amber-700 font-bold rounded-lg hover:bg-amber-50 transition-colors shadow-lg"
            >
              {t('premium.hero_cta', 'Assinar agora')}
            </Link>
          </div>
          <p className="mt-3 text-xs text-white/70">
            {t('premium.hero_note', 'Cartão de crédito · Renovação mensal · Cancele quando quiser')}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-12 sm:py-16 space-y-16">
        {/* Benefícios */}
        <section>
          <div className="text-center mb-10">
            <span className="text-xs font-semibold text-amber-600 uppercase tracking-widest">
              {t('premium.benefits_eyebrow', 'O que você ganha')}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t('premium.benefits_title', 'As vantagens do Premium')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Benefit
              icon={<Sparkles className="w-6 h-6" />}
              title={t('premium.b1_title', 'Sem anúncios')}
              description={t('premium.b1_desc', 'Navegue pela plataforma sem nenhum banner ou anúncio. Experiência limpa, foco total no bolão.')}
            />
            <Benefit
              icon={<LayoutGrid className="w-6 h-6" />}
              title={t('premium.b2_title', 'Bolões privados ilimitados')}
              description={t('premium.b2_desc', 'Crie quantos bolões privados quiser — um para cada competição, um para cada grupo de amigos.')}
            />
            <Benefit
              icon={<Users className="w-6 h-6" />}
              title={t('premium.b3_title', 'Participantes ilimitados')}
              description={t('premium.b3_desc', 'Seus bolões privados não têm teto de participantes. Convide o grupo inteiro, a família, a empresa toda.')}
            />
            <Benefit
              icon={<Gift className="w-6 h-6" />}
              title={t('premium.b4_title', 'Quem você convida não paga')}
              description={t('premium.b4_desc', 'Só o criador precisa ser Premium. Todo mundo que você convida entra de graça, com conta gratuita.')}
            />
            <Benefit
              icon={<Layers className="w-6 h-6" />}
              title={t('premium.b5_title', 'Participe de quantos bolões quiser')}
              description={t('premium.b5_desc', 'Entre em quantos bolões de outras pessoas você quiser, sem limite de participação.')}
            />
            <Benefit
              icon={<Rocket className="w-6 h-6" />}
              title={t('premium.b6_title', 'Prioridade em novidades')}
              description={t('premium.b6_desc', 'Recursos e funcionalidades novas chegam primeiro para os assinantes Premium.')}
            />
          </div>
        </section>

        {/* Comparativo Free × Premium */}
        <section>
          <div className="text-center mb-8">
            <span className="text-xs font-semibold text-primary-600 uppercase tracking-widest">
              {t('premium.compare_eyebrow', 'Comparativo')}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t('premium.compare_title', 'Free × Premium')}
            </h2>
            <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">
              {t('premium.compare_subtitle', 'Tudo o que está no plano gratuito continua gratuito. O Premium apenas remove os limites — você só assina se precisar de mais.')}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 sm:px-6 py-4 font-semibold text-gray-700">{t('premium.compare_col_feature', 'Funcionalidade')}</th>
                  <th className="text-center px-3 sm:px-6 py-4 font-semibold text-gray-700">{t('premium.compare_col_free', 'Free')}</th>
                  <th className="text-center px-3 sm:px-6 py-4 font-semibold text-amber-700">{t('premium.compare_col_premium', 'Premium')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <CompareRow feature={t('premium.cmp1_feature', 'Bolões privados que você cria')} free={String(limits.maxPrivatePoolsCreated)} premium={t('premium.unlimited_plural', 'Ilimitados')} />
                <CompareRow feature={t('premium.cmp2_feature', 'Participantes nos seus bolões privados')} free={t('premium.up_to', 'até {{n}}', { n: limits.maxPrivatePoolMembers })} premium={t('premium.unlimited_plural', 'Ilimitados')} />
                <CompareRow feature={t('premium.cmp3_feature', 'Entrar em bolões privados de amigos')} free={t('premium.up_to', 'até {{n}}', { n: limits.maxPrivatePoolsJoined })} premium={t('premium.unlimited_plural', 'Ilimitados')} />
                <CompareRow feature={t('premium.cmp4_feature', 'Participar de bolões públicos')} free={t('premium.unlimited', 'Ilimitado')} premium={t('premium.unlimited', 'Ilimitado')} />
                <CompareRow feature={t('premium.cmp5_feature', 'Palpitar em todas as competições')} free={true} premium={true} />
                <CompareRow feature={t('premium.cmp6_feature', 'Ranking, conquistas e notificações')} free={true} premium={true} />
                <CompareRow feature={t('premium.cmp7_feature', 'Navegar sem anúncios')} free={false} premium={true} />
                <CompareRow feature={t('premium.cmp8_feature', 'Prioridade em novas funcionalidades')} free={false} premium={true} />
              </tbody>
            </table>
          </div>
        </section>

        {/* Pagamento + cancelamento */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <InfoCard
            icon={<CreditCard className="w-6 h-6" />}
            title={t('premium.pay_title', 'Como funciona o pagamento')}
          >
            <p>{t('premium.pay_p1', 'Cobrança mensal automática no cartão de crédito cadastrado. Você só paga enquanto quiser — sem multa, sem fidelidade.')}</p>
            <p>{t('premium.pay_p2', 'Tem um cupom de desconto? É só aplicar na hora de assinar, dentro do app.')}</p>
          </InfoCard>
          <InfoCard
            icon={<RefreshCw className="w-6 h-6" />}
            title={t('premium.cancel_title', 'Cancelamento simples')}
          >
            <p>
              <Trans i18nKey="premium.cancel_p1" t={t}>
                Cancele em <em>Minha conta → Assinatura</em> com 1 clique, sem precisar falar com ninguém.
              </Trans>
            </p>
            <p>{t('premium.cancel_p2', 'Ao cancelar, você mantém todos os benefícios Premium até o fim do ciclo já pago. Depois disso a conta volta ao plano gratuito.')}</p>
          </InfoCard>
        </section>

        {/* FAQ Premium */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t('premium.faq_title', 'Perguntas frequentes')}
            </h2>
          </div>
          <div className="space-y-3 max-w-3xl mx-auto">
            <Faq question={t('premium.faq_1_q', 'Quanto custa o Bolão Elite Premium?')}>
              {t('premium.faq_1_a', 'R$ 9,90 por mês, cobrados no cartão de crédito com renovação automática. Sem fidelidade — você cancela quando quiser.')}
            </Faq>
            <Faq question={t('premium.faq_2_q', 'O que muda quando eu assino o Premium?')}>
              {t('premium.faq_2_a', 'Você passa a criar bolões privados ilimitados, com quantos participantes quiser em cada um, navega sem anúncios e participa de quantos bolões de outras pessoas desejar.')}
            </Faq>
            <Faq question={t('premium.faq_3_q', 'Os participantes dos meus bolões precisam ser Premium?')}>
              {t('premium.faq_3_a', 'Não. Qualquer pessoa com conta gratuita entra normalmente nos seus bolões. O Premium é só do criador — quem participa não paga nada.')}
            </Faq>
            <Faq question={t('premium.faq_4_q', 'O Bolão Elite tem versão gratuita?')}>
              {t('premium.faq_4_a', {
                defaultValue:
                  'Sim. No plano gratuito você cria {{count}} {{noun}} com até {{members}} participantes, participa de até {{joined}} bolões privados de amigos e de bolões públicos sem limite — além de palpitar em todas as competições, ver ranking, conquistas e notificações.',
                count: limits.maxPrivatePoolsCreated,
                noun: poolNoun,
                members: limits.maxPrivatePoolMembers,
                joined: limits.maxPrivatePoolsJoined,
              })}
            </Faq>
            <Faq question={t('premium.faq_5_q', 'Como funciona a renovação automática?')}>
              {t('premium.faq_5_a', 'A assinatura é mensal e renova automaticamente no cartão cadastrado. Você pode desabilitar a renovação a qualquer momento — a conta segue Premium até o fim do período já pago.')}
            </Faq>
            <Faq question={t('premium.faq_6_q', 'Tenho um cupom de desconto. Como uso?')}>
              {t('premium.faq_6_a', 'Na página de assinatura dentro do app, digite o código no campo "Cupom de desconto" e toque em "Aplicar" antes de finalizar. O preço final é atualizado na hora.')}
            </Faq>
          </div>
        </section>

        {/* CTA final */}
        <section className="bg-gradient-to-r from-amber-500 to-amber-700 rounded-2xl p-6 sm:p-10 text-white text-center">
          <Crown className="w-10 h-10 mx-auto mb-3 opacity-80" />
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">
            {t('premium.cta_title', 'Pronto para tirar os limites?')}
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {t('premium.cta_text', 'R$ 9,90/mês. Cancele quando quiser, sem fidelidade. Bolões e participantes ilimitados, navegação sem anúncios.')}
          </p>
          <Link
            to="/assinatura"
            className="inline-block px-8 py-3 bg-white text-amber-700 font-bold rounded-lg hover:bg-amber-50 transition-colors shadow-lg"
          >
            {t('premium.cta_button', 'Assinar Premium')}
          </Link>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function Benefit({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-amber-300 hover:shadow-md transition-all">
      <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

type CompareValue = boolean | string;

function CompareCell({ value, color }: { value: CompareValue; color: 'emerald' | 'amber' }) {
  const { t } = useTranslation('institutional');
  if (typeof value === 'string') {
    const colorClass = color === 'emerald' ? 'text-emerald-700' : 'text-amber-700';
    return <span className={`text-xs font-bold ${colorClass}`}>{value}</span>;
  }
  if (value) {
    const colorClass = color === 'emerald' ? 'text-emerald-600' : 'text-amber-600';
    return <Check className={`w-5 h-5 ${colorClass} inline`} aria-label={t('premium.included', 'Incluído')} />;
  }
  return <X className="w-5 h-5 text-gray-300 inline" aria-label={t('premium.not_included', 'Não incluído')} />;
}

function CompareRow({ feature, free, premium }: { feature: string; free: CompareValue; premium: CompareValue }) {
  return (
    <tr>
      <td className="px-4 sm:px-6 py-3 text-gray-700">{feature}</td>
      <td className="px-3 sm:px-6 py-3 text-center"><CompareCell value={free} color="emerald" /></td>
      <td className="px-3 sm:px-6 py-3 text-center"><CompareCell value={premium} color="amber" /></td>
    </tr>
  );
}

function InfoCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6">
      <div className="w-11 h-11 bg-primary-100 text-primary-700 rounded-xl flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
      <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function Faq({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <details className="group bg-white border border-gray-200 rounded-xl">
      <summary className="cursor-pointer p-4 font-semibold text-sm text-gray-900 flex items-center justify-between list-none">
        <span>{question}</span>
        <span className="text-gray-400 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
        {children}
      </div>
    </details>
  );
}
